export const policyMeta = {
  version: '1.0',
  effectiveDate: '15 July 2026',
  operator: 'ZND Ventures LLP',
  contact: 'support@sawwari.com',
};

export const legalDocuments = {
  terms: {
    title: 'Terms of Service',
    summary: 'The rules for using Sawwari ride, intercity, courier, logistics and account services in Pakistan.',
    sections: [
      {
        title: '1. Agreement and operator',
        paragraphs: [
          'These Terms form a binding electronic agreement between you and ZND Ventures LLP, operating the Sawwari platform in Pakistan. By creating an account, accepting a displayed version of these Terms or using a Sawwari service, you confirm that you have read and accepted them.',
          'Sawwari is a technology platform that connects eligible users with independent Captains and enables related booking, communication, safety, support, record and payment-facilitation functions. Service availability varies by city, route, category, operating permission and Captain supply.',
        ],
      },
      {
        title: '2. Eligibility and accounts',
        paragraphs: ['You must provide accurate information and keep your account secure. A passenger account holder must be at least 18 years old, or use the service through and under the responsibility of an adult permitted by law. You may not transfer, sell or lend your account.'],
        bullets: [
          'Complete mobile-number OTP verification and any identity or liveness checks reasonably required for safety, fraud prevention or legal compliance.',
          'Keep your name, contact details and emergency information current.',
          'Notify Sawwari promptly of unauthorized access, a lost device or suspected misuse.',
          'Do not create duplicate or misleading accounts to evade a restriction, debt, investigation or safety decision.',
        ],
      },
      {
        title: '3. Ride and intercity requests',
        paragraphs: [
          'A request is an invitation to an eligible Captain. A journey contract is formed between the passenger and the accepting Captain when the request is accepted, subject to these Terms and applicable law. Sawwari may present category, Captain, vehicle, route, fare and estimated-arrival information, but traffic, weather, road closures, checkpoints and other conditions can change actual results.',
          'City-to-city travel is an active Sawwari service where enabled. Passengers and Captains must use lawful routes, observe seat and luggage limits, and comply with any inter-provincial, motorway, toll, permit or local transport requirement that applies to the journey.',
        ],
      },
      {
        title: '4. User conduct and safety',
        bullets: [
          'Treat Captains, passengers, recipients, staff and the public respectfully; harassment, discrimination, threats, violence and sexual misconduct are prohibited.',
          'Do not request illegal activity, unsafe driving, overloading, route evasion, cash manipulation or transport of a prohibited item.',
          'Use the seat and safety equipment required by law and follow reasonable safety directions.',
          'For immediate danger call Police Emergency 15. Sawwari support is not an emergency-response service.',
        ],
      },
      {
        title: '5. Fares, payments and platform records',
        paragraphs: [
          'Before confirmation, Sawwari may show an estimate or agreed fare and any disclosed toll, waiting or other charge. Payment may be supported through cash, Easypaisa, JazzCash or another enabled method. Digital payments are processed by the relevant regulated provider under its own terms; Sawwari is not a bank, electronic-money institution, PSO or PSP unless separately licensed and expressly identified as such.',
          'Platform records, including timestamps, GPS and route signals, acceptance events, communications, payment references and support evidence, may be used to reconcile a journey, investigate a complaint or apply the Fares, Cancellations & Refunds Policy.',
        ],
      },
      {
        title: '6. Suspension and termination',
        paragraphs: ['Sawwari may warn, restrict, suspend or terminate access where reasonably necessary for safety, fraud prevention, unpaid obligations, repeated cancellations, prohibited conduct, document failure, legal compliance or investigation. Where appropriate, we will provide a reason and a review path. Urgent protective action may be taken before notice.'],
      },
      {
        title: '7. Liability and mandatory rights',
        paragraphs: [
          'Sawwari does not guarantee uninterrupted availability, a match, a particular route, arrival time or outcome. Nothing in these Terms excludes liability that cannot lawfully be excluded, or limits rights available under applicable federal, provincial or territorial consumer law.',
          'To the extent permitted by law, Sawwari is not responsible for indirect or consequential loss, or loss caused by a user, Captain, third-party provider, unlawful item, inaccurate information, network outage or event beyond reasonable control. Each claim remains subject to its facts and applicable law.',
        ],
      },
      {
        title: '8. Complaints, governing law and changes',
        paragraphs: [
          'Submit complaints through Sawwari support with the relevant reference and evidence. These Terms are governed by the laws of Pakistan. A dispute may be taken to a court, consumer forum or regulator with lawful jurisdiction; nothing here removes a mandatory forum or remedy.',
          'We may update these Terms for legal, safety, product or operational reasons. Material changes will be versioned and notified in the app or by another appropriate channel. Continued use after the effective date constitutes acceptance where permitted by law; fresh acceptance will be requested when required.',
        ],
      },
      {
        title: '9. Legal framework',
        paragraphs: ['These Terms are intended to operate consistently with the Contract Act, 1872; Electronic Transactions Ordinance, 2002; Prevention of Electronic Crimes Act, 2016 as amended; Payment Systems and Electronic Fund Transfers Act, 2007 for relevant provider-handled payments; and applicable provincial or territorial consumer, motor-vehicle, transport-network, courier, tax and safety rules. A reference includes lawful amendments and replacement legislation.'],
      },
    ],
  },

  privacy: {
    title: 'Privacy Notice',
    summary: 'How Sawwari collects, uses, shares, protects and retains personal and operational information.',
    sections: [
      {
        title: '1. Scope and responsibility',
        paragraphs: ['ZND Ventures LLP is responsible for the personal information processed through Sawwari passenger, Captain, courier, logistics, website, support and administrative services. This Notice applies in Pakistan and should be read with the Terms and any just-in-time consent shown in the app.'],
      },
      {
        title: '2. Information we collect',
        bullets: [
          'Account information: name, mobile number, email, date of birth, address, profile image and account credentials.',
          'Identity and eligibility information: CNIC details and images, live selfie or liveness result, driving licence, vehicle registration, ownership, permits, insurance and review history where applicable.',
          'Journey information: pickup, destination, precise location while searching or travelling, route, timestamps, category, fare, cancellation and safety events.',
          'Courier information: sender and recipient details, declared contents, packaging, handover and delivery confirmation.',
          'Device and security information: device identifiers, IP address, app version, logs, OTP events, fraud signals and consent records.',
          'Payment and support information: payment method, provider reference, wallet or ledger entries, complaints, messages, attachments and call or communication metadata where lawfully enabled.',
        ],
      },
      {
        title: '3. Why we use information',
        bullets: [
          'Create and secure accounts; verify identity, documents and eligibility.',
          'Match requests, calculate routes and fares, show live trip progress and complete rides or deliveries.',
          'Process or reconcile payments through enabled providers and maintain accurate financial records.',
          'Prevent fraud, protect users, investigate incidents, resolve complaints and enforce platform rules.',
          'Maintain, troubleshoot and improve reliability, accessibility, safety and service coverage.',
          'Comply with lawful requests, court orders, tax, transport, financial, cybercrime and record-preservation duties.',
        ],
      },
      {
        title: '4. Location, identity and sensitive data',
        paragraphs: [
          'Precise location is necessary for matching, navigation, live journey visibility, safety and evidence. Active-trip location may continue while the app is in the background where the device permission allows it. You can control device permissions, but disabling necessary location access can prevent booking or Captain functions.',
          'CNIC images, live selfies, liveness results and similar identity data receive restricted access and are used only for verification, safety, fraud prevention, legal compliance and authorized review. Sawwari does not sell biometric or identity information.',
        ],
      },
      {
        title: '5. Sharing and disclosures',
        paragraphs: ['We disclose only what is reasonably necessary to operate or protect the service.'],
        bullets: [
          'Between matched users: limited name, profile, vehicle, contact and journey information needed to complete the service.',
          'To payment, OTP, mapping, hosting, verification, notification, analytics and support vendors acting under contractual and security controls.',
          'To insurers, professional advisers or business counterparties where necessary for a claim, audit, restructuring or lawful transaction.',
          'To police, courts, transport authorities, tax authorities or another competent body where required by law, lawful process or an urgent threat to life or safety.',
        ],
      },
      {
        title: '6. Retention and security',
        paragraphs: [
          'We retain information only for as long as needed for the service, safety, disputes, fraud prevention, accounting, tax, transport records, legal claims and other lawful obligations. Retention periods differ by record type; deletion is suspended where a record is subject to a complaint, investigation, legal hold or unpaid obligation.',
          'We use role-based access, encryption in transit, credential protection, audit logging, backups and monitoring proportionate to the risk. No system is completely secure; report suspected compromise promptly.',
        ],
      },
      {
        title: '7. Your choices and requests',
        bullets: [
          'Request access to, correction of or deletion of your account information, subject to identity verification and lawful retention.',
          'Withdraw an optional consent or marketing preference without affecting earlier lawful processing.',
          'Control location, camera and notification permissions through your device, understanding that core features may stop working.',
          'Challenge an automated or administrative restriction through support and provide relevant evidence.',
        ],
      },
      {
        title: '8. Children, transfers and contact',
        paragraphs: [
          'Sawwari accounts are not directed to children acting independently. A parent or lawful guardian should contact us if a child has supplied personal information without appropriate authority.',
          'Where a service provider processes data outside Pakistan, Sawwari applies contractual, access and security controls and makes disclosures required by applicable law. Privacy requests may be sent to support@sawwari.com. We may verify identity before acting on a request.',
        ],
      },
      {
        title: '9. Applicable privacy law',
        paragraphs: ['Sawwari applies this Notice as a contractual privacy standard in addition to duties arising under applicable Pakistani law, including the Electronic Transactions Ordinance, 2002 and Prevention of Electronic Crimes Act, 2016 as amended. Where a later data-protection law or binding rule applies, this Notice will be interpreted and updated consistently with it.'],
      },
    ],
  },

  'fares-cancellations-refunds': {
    title: 'Fares, Cancellations & Refunds',
    summary: 'Transparent rules for estimates, agreed fares, cancellation review, payment disputes and approved refunds.',
    sections: [
      {
        title: '1. Fare information',
        paragraphs: ['Sawwari will display the applicable pricing method before confirmation where practicable. A price may be an estimate, a fixed offer or an amount agreed through an enabled flow. The final amount may include disclosed waiting, toll, parking, route-change or other lawful charges. No undisclosed charge may be demanded through the platform.'],
      },
      {
        title: '2. Payment methods',
        paragraphs: ['Enabled methods may include cash, Easypaisa and JazzCash. A passenger does not need a positive Sawwari wallet balance merely to search for a ride. A digital provider may apply its own authorization, reversal and settlement rules. Keep the provider transaction reference until the journey is reconciled.'],
      },
      {
        title: '3. Cancellations and no-shows',
        bullets: [
          'A charge, if any, must be shown or explained under the active city/category configuration and must be consistent with applicable law.',
          'Safety concerns, vehicle or Captain mismatch, material delay, duplicate booking, platform failure and other supported reasons may qualify for waiver or review.',
          'Repeated intentional cancellations, false no-show reports or off-platform fare manipulation may result in restriction after evidence review.',
          'GPS loss alone does not prove fault. It is treated as a risk signal and may place the matter into pending review.',
        ],
      },
      {
        title: '4. Captain commission reserve',
        paragraphs: ['After the final fare is accepted, Sawwari may reserve the disclosed platform commission from the Captain wallet or ledger. The reserve remains auditable while a cancellation, payment or trip-completion issue is reviewed. A passenger cancellation does not automatically determine Captain fault or automatically release or forfeit the reserve.'],
      },
      {
        title: '5. Evidence and decisions',
        paragraphs: ['Sawwari may consider acceptance events, timestamps, GPS and route signals, arrival proximity, communications, payment references, safety reports and information supplied by both parties. A decision may be approved, rejected or held pending further evidence. Users may ask for review through the same complaint record.'],
      },
      {
        title: '6. Refunds and reversals',
        paragraphs: [
          'An approved digital refund is returned to the original method where technically possible; provider processing time applies. Cash adjustments are handled through a verified method stated in the resolution. Sawwari will not ask for an OTP, PIN or password to issue a refund.',
          'A bank or wallet reversal does not by itself settle the underlying fare dispute. Duplicate, unauthorized or failed payments should be reported with the journey and provider references. Mandatory rights under consumer and payment law remain unaffected.',
        ],
      },
    ],
  },

  'captain-terms': {
    title: 'Captain Terms',
    summary: 'The service, compliance, payment and conduct terms for independent Sawwari Captains in Pakistan.',
    sections: [
      {
        title: '1. Independent service provider',
        paragraphs: ['A Captain supplies transport or delivery services as an independent service provider and not as an employee, worker, partner, agent or representative of ZND Ventures LLP. Nothing authorizes a Captain to bind Sawwari, make promises for Sawwari or present Sawwari as the owner or operator of the Captain’s vehicle. Mandatory rights that apply by law are not waived by this description.'],
      },
      {
        title: '2. Eligibility and local authorization',
        paragraphs: ['Approval in the app does not replace any licence, permit, registration, fitness certificate, route permission, insurance, tax registration or authorization required by federal, provincial, territorial or local law. A Captain may go online only in an area and category for which the Captain, vehicle and Sawwari service are lawfully enabled.'],
        bullets: [
          'Maintain a valid CNIC, driving licence and category-appropriate eligibility.',
          'Maintain lawful vehicle registration, roadworthiness, ownership or written authority to use the vehicle, and required insurance or permits.',
          'Upload genuine, complete and current documents and promptly report expiry, suspension, seizure, accident or ownership change.',
          'Do not substitute a driver or vehicle without platform approval.',
        ],
      },
      {
        title: '3. Service standards',
        bullets: [
          'Accept only requests you can lawfully and safely complete; use the requested category and verified vehicle.',
          'Do not discriminate, harass, solicit unlawful activity, manipulate fares, demand an undisclosed amount or pressure a user to cancel.',
          'Observe traffic, seat, helmet, loading, working-condition and route rules; never use the app in a way that distracts from driving.',
          'For courier work, inspect or refuse a suspicious item, require accurate declaration and follow the prohibited-items policy.',
        ],
      },
      {
        title: '4. Fares, commission, wallet and tax',
        paragraphs: [
          'The Captain sees the applicable fare method, platform commission and any incentive conditions through the app or active schedule. After final fare acceptance, Sawwari may reserve and settle the commission through the Captain wallet or ledger. Entries are auditable and may be held during a genuine dispute, fraud review or lawful investigation.',
          'The Captain is responsible for personal and business taxes, vehicle costs, fuel, maintenance, permits, insurance, mobile data and other operating expenses unless a written Sawwari offer says otherwise. Sawwari may make deductions or provide records where required by tax law.',
        ],
      },
      {
        title: '5. Safety, data and investigations',
        paragraphs: ['The Captain consents to processing of identity, document, device, live-location, route, acceptance, communication, safety and payment information for platform operation and the purposes in the Privacy Notice. Sawwari may preserve evidence, pause access or payouts and seek information while a serious matter is reviewed. GPS loss is not conclusive proof of misconduct.'],
      },
      {
        title: '6. Suspension, review and exit',
        paragraphs: ['Sawwari may restrict or remove access for expired or false documents, unsafe conduct, fraud, unlawful service, repeated material complaints, unpaid commission or legal direction. Urgent action may precede notice. Where appropriate, the Captain will receive a reason and a review path. Either party may end the platform relationship, but accrued payments, investigations, records and legal duties survive.'],
      },
      {
        title: '7. Governing law and disputes',
        paragraphs: ['These Captain Terms are governed by Pakistani law together with the locally applicable transport and labour classification rules. Disputes should first be raised through the traceable complaints process and may then be taken to a competent court or authority.'],
      },
    ],
  },

  'courier-prohibited-items': {
    title: 'Courier Prohibited & Restricted Items',
    summary: 'What cannot travel through Sawwari Courier and the responsibilities at sender-to-Captain handover.',
    sections: [
      {
        title: '1. Scope',
        paragraphs: ['Sawwari Courier is for eligible, declared and securely packaged items within the same city unless the app expressly enables another service. The sender is responsible for the contents and for having the legal right to send them. The Captain transports the parcel but does not become its owner or certify its legality.'],
      },
      {
        title: '2. Prohibited items',
        bullets: [
          'Weapons, ammunition, explosives, fireworks, detonators or components intended for them.',
          'Narcotic drugs, controlled substances, illegal medicines, intoxicants transported contrary to law, or drug paraphernalia.',
          'Stolen, smuggled, counterfeit, pirated, obscene, abusive or otherwise unlawful goods or content.',
          'Human remains, body parts, biological specimens, infectious material or medical waste.',
          'Hazardous chemicals, fuel, gas cylinders, corrosives, poisons, radioactive material, loose batteries or any item presenting fire, leak or contamination risk.',
          'Live animals, protected wildlife, plants or agricultural material whose movement is prohibited or requires a specialist permit.',
          'Cash, bearer instruments, bank cards, passwords, OTP devices, precious metals, jewellery or another high-value item not expressly accepted by Sawwari.',
          'Any item intended to facilitate violence, fraud, identity theft, harassment, terrorism or another offence.',
        ],
      },
      {
        title: '3. Restricted items',
        paragraphs: ['Medicines, food, fragile goods, electronics, identity documents, keys, perishables and valuable documents may be refused or accepted only where the app states the conditions. Sawwari Courier does not provide refrigeration, sterile handling, dangerous-goods handling, insurance valuation or specialist medical transport unless expressly stated.'],
      },
      {
        title: '4. Packaging, declaration and inspection',
        bullets: [
          'Describe the contents, quantity, approximate size and handling needs accurately.',
          'Use packaging suitable for normal road movement and prevent leakage, injury, tampering or damage.',
          'Seal the parcel in the Captain’s presence when requested and keep any handover evidence.',
          'Permit a reasonable visual inspection before sealing. A Captain must not open a sealed parcel after acceptance except with consent, in an emergency or under lawful authority.',
        ],
      },
      {
        title: '5. Refusal, reporting and liability',
        paragraphs: ['A Captain or Sawwari may refuse, cancel, isolate or report a suspicious, unsafe, falsely declared or prohibited parcel. Where law or immediate safety requires it, information and the item may be provided to competent authorities. The sender remains responsible for loss, injury, penalties and claims caused by unlawful contents, false declaration or inadequate packaging, subject to applicable law.'],
      },
      {
        title: '6. Delivery and complaints',
        paragraphs: ['Recipient confirmation, PIN, signature, photograph or another enabled proof may be required. Report non-delivery, tampering or damage promptly through support and preserve packaging, photographs and the order reference. Any compensation is determined from the evidence, declared service conditions and applicable law.'],
      },
    ],
  },

  'account-deletion': {
    title: 'Account Deletion',
    summary: 'How to close a Sawwari account and what records may lawfully remain after closure.',
    sections: [
      {
        title: '1. Submit a request',
        paragraphs: ['Request deletion through the in-app account controls or email support@sawwari.com from your registered contact channel. State the registered mobile number and do not send your OTP, PIN or password. Sawwari will verify identity before acting.'],
      },
      {
        title: '2. Before deletion',
        bullets: [
          'Complete or cancel active rides, courier orders and support cases.',
          'Settle any lawful fare, commission, wallet or refund balance.',
          'Download information you need, because access ends after deletion.',
          'A Captain must resolve active document, safety, payout or regulatory reviews.',
        ],
      },
      {
        title: '3. Processing and effect',
        paragraphs: ['We normally acknowledge the request promptly and complete eligible deletion within 30 days after verification. The account is deactivated, access credentials are disabled and information no longer needed is deleted or de-identified. A request may be paused while identity, an active service, an unpaid balance or a legal hold is resolved.'],
      },
      {
        title: '4. Records we may retain',
        paragraphs: ['Deletion does not require removal of records that must or may lawfully be retained for tax, accounting, transport regulation, payment reconciliation, fraud prevention, safety, complaints, litigation, court orders or law-enforcement purposes. Access to retained records is restricted and they are deleted or de-identified when the retention purpose ends.'],
      },
      {
        title: '5. Re-registration and review',
        paragraphs: ['A deleted account may not be restorable. A new account remains subject to verification and may not be used to evade a restriction, debt or investigation. If we cannot complete a request, we will explain the reason where legally permitted and provide a support review path.'],
      },
    ],
  },

  complaints: {
    title: 'Complaints & Resolution Policy',
    summary: 'A traceable process for complaints, evidence, review, safety escalation and regulatory rights.',
    sections: [
      {
        title: '1. How to complain',
        paragraphs: ['Use the in-app complaints flow or support@sawwari.com. Include the ride, courier, payment or account reference; what happened; when and where it happened; the outcome requested; and relevant evidence. Never send an OTP, PIN or password.'],
      },
      {
        title: '2. Ticket record and timing',
        paragraphs: ['Each complaint receives a traceable reference. We aim to acknowledge it within 24 hours and provide a substantive response within 7 working days. Complex safety, payment, fraud or authority-linked matters may take longer; we will provide a status update when practicable. These are service targets and do not shorten a statutory deadline or limitation period.'],
      },
      {
        title: '3. Evidence and fairness',
        paragraphs: ['Sawwari may review account and journey events, GPS and route signals, documents, communications, payment references, photographs, attachments and statements from affected parties. GPS interruption alone is not treated as conclusive fault. Relevant parties should have a reasonable opportunity to respond unless immediate safety, fraud prevention or law prohibits it.'],
      },
      {
        title: '4. Possible outcomes',
        bullets: [
          'Explanation, correction of a record or service guidance.',
          'Fare adjustment, refund, wallet or commission correction where supported.',
          'Warning, training, document re-verification, temporary restriction or account removal.',
          'Preservation or disclosure of evidence to a competent authority where lawfully required.',
          'Pending status where material evidence or an external-provider result is outstanding.',
        ],
      },
      {
        title: '5. Review and external rights',
        paragraphs: ['If you disagree, reply under the same ticket and identify the factual or policy issue to be reconsidered. You remain free to approach a competent police service, transport authority, payment provider, consumer forum, court or regulator. For immediate danger call 15 before contacting Sawwari.'],
      },
      {
        title: '6. Confidentiality and retaliation',
        paragraphs: ['Complaint information is shared only as needed to investigate, protect users, comply with law or provide a response. Retaliation, threats, false evidence and witness manipulation are prohibited and may result in account action or referral to authorities.'],
      },
    ],
  },
};

export const canonicalLegalLinks = [
  ['Terms of Service', '/terms'],
  ['Privacy Notice', '/privacy'],
  ['Fares, Cancellations & Refunds', '/fares-cancellations-refunds'],
  ['Captain Terms', '/captain-terms'],
  ['Courier Prohibited Items', '/courier-prohibited-items'],
  ['Complaints Policy', '/complaints'],
  ['Account Deletion', '/account-deletion'],
];
